
export const DEFAULT_PROMPTS = [
  {
    category: "industryTrends",
    label: "Analyze Industry Trends",
    prompt:
      "Please analyze the industry trends from the provided document and identify key patterns, emerging technologies, and market shifts that could impact the organization.",
  },
  {
    category: "workforceImpact",
    label: "Assess Workforce Impact",
    prompt:
      "Based on the industry trends identified, what will be the impact on our workforce and skills development needs? Please provide a detailed analysis.",
  },
  {
    category: "training",
    label: "Training Recommendations",
    prompt:
      "What training and development initiatives should we implement to address the identified workforce impact and skills gaps? Please provide specific recommendations.",
  },
  {
    category: "strategicSummary",
    label: "Strategic Summary",
    prompt:
      "Please provide a comprehensive strategic summary of the industry trends, workforce impact, and training recommendations identified in the document.",
  },
  {
    category: "criticalSkills",
    label: "Future & Critical Skills Identification",
    prompt:
      "Analyze the organization and industry context to identify future and critical skills that will be necessary for the company's continued growth and success. For each skill identified, provide the following details:\n\n" +
      "1. **Skill Name**: The name of the critical skill\n" +
      "2. **Level**: Classify the skill level as one of the following:\n" +
      "   - 'essential': Basic or foundational skills required for the role\n" +
      "   - 'intermediate': Skills that require some experience and knowledge\n" +
      "   - 'expert': Advanced skills requiring deep expertise and mastery\n" +
      "3. **Importance**: Rate the importance on a scale of 1 to 5, where:\n" +
      "   - 1 = Low importance\n" +
      "   - 2 = Below average importance\n" +
      "   - 3 = Average importance\n" +
      "   - 4 = High importance\n" +
      "   - 5 = Critical importance (essential for success)\n\n" +
      "Present the skills in a clear, structured format with each skill including its name, level, and importance rating. Focus on emerging needs and long-term capabilities that align with strategic workforce planning priorities.",
  },
  {
    category: "allocationSummary",
    label: "Allocation Summary",
    prompt: "You are an AI assistant tasked with calculating allocation summary statistics based on training allocations, employee demographics, and budget configurations."
  },
  {
    category: "groupRequestList",
    label: "Group Request List",
    prompt:`You are an AI classification and aggregation engine.

Your task is to classify, group, and summarize employee training requests into ONE primary topic per request and produce an aggregated demand report with full traceability.

TOPIC LIST (choose exactly one per request):

Leadership & Management
Communication Skills
Customer Relations
Sales & Marketing
Digital & IT Skills
Data & Analytics
Project & Programme Management
Compliance & Governance
Health & Safety
Finance & Accounting
HR & People Practices
Personal Effectiveness & Productivity
Technical & Vocational Skills
Diversity Equity & Inclusion
Customer Experience (CX)
Change Management
Emotional Intelligence & Wellbeing
Innovation & Creative Thinking
Workplace Readiness
Entrepreneurship & Business Skills
Other

CLASSIFICATION & GROUPING RULES (STRICT):

Assign exactly ONE topic to each training request.

Formal finance or commerce qualifications (degrees, diplomas, postgraduate studies) must be classified as Finance & Accounting.

Accounting or finance software training (for example, Sage Accounting) must also be classified as Finance & Accounting, not Digital & IT Skills.

Group similar training requests into ONE aggregated entry (for example, similar qualifications or similar tools).

Each aggregated entry must include all training request IDs that were grouped together.

Create a short, meaningful trainingName that represents the grouped requests (use a subtopic-style label, not a full sentence).

Combine motivations from all grouped requests into ONE clear summarized sentence.

program_duration must be the same for all requests in the group. Do NOT mix different durations in one group.

Sort the final output by the number of training requests in each group (descending order).

INPUT:

You will receive a JSON array of training requests. Each training request object contains the following fields:
- "id": string (unique identifier - MUST be preserved exactly in trainingRequestIds)
- "trainingName": string (the name of the training requested)
- "motivation": string (reason for the training request)
- "program_duration": string (duration type: "qualification", "short_program", etc.)
- "employeeName": string (name of the employee requesting)
- "department": string (department of the employee)

Analyze the provided training requests array and apply the rules above.

OUTPUT REQUIREMENTS (MANDATORY & STRICT):

Return ONLY a valid JSON array.
Do NOT include markdown, explanations, or extra text.

Each item in the array must follow this structure exactly:

[
  {
    "topic": "string",
    "trainingName": "string",
    "trainingRequestIds": ["string"],
    "motivation": "string",
    "program_duration": "string"
  }
]

STRICT OUTPUT RULES:

- trainingRequestIds must contain ONLY exact "id" values from the input.
- Do NOT generate new IDs or ordinal values.
- program_duration must be identical for all grouped requests.
- If a rule is violated, fix the output before responding.
- Return an empty array ONLY when the input training request list is empty.`
    
  },
  {
    category: "planOptimizerDataTransformation",
    label: "Plan Optimizer Data Transformation",
    prompt: `You are a data transformation assistant.
        You will receive:
        1. A Seta template schema that defines the expected field names and their types.
        2. A list of employees with their allocated training programs.

        Additional instructions:
        - When mapping fields, if the employee data contains "emp_Id" or "dept_id", keep these fields as they are in the output (do not rename or transform them).

        Your task:
        - Generate one output object **per training program per employee**.
        - Use the Seta template schema to correctly map fields.
        - Beside the Seta template include id which was given with employee details.
        - Make sure the output is an **array of objects**, one for each program per employee, each object using the **exact Seta field names** as keys (not "fieldName").
        - In addition to the array of objects, also return the total cost/expense of all allocated training programs as a separate field named "totalCost" (e.g., { "data": [...], "totalCost": ... }).
        - Do **not** return any explanation or markdown. Return only raw JSON in the format: { "data": [ { ... }, ... ], "totalCost": ... }
        - Follow these rules:
          - Map employee fields like "first_name" and "last_name" to "Name" and "Surname".
          - Use training program details to fill fields like "Training Required [Learning Intervention Name/Description]", etc.
          - Skill requirements details are also given and take the skills details from that array based on the skill requirement id. Use the skill requirements data to populate fields like "Training Needs", "Why is the training required", or similar fields that ask about training needs or motivation.
          - Ensure field values match allowed dropdown options wherever applicable. If the field is a dropdown, first determine the meaning of the user's input (e.g., the meaning of "Caucasian" is "white"). Then, match this meaning with the available options in the dropdown. If a similar meaning word is found in the options, return that value. If no similar meaning word is found in the options, return "others".
          - If a field is missing or unclear, skip it or leave it blank.
          - If the allocated training programs is empty array then return '-' to respected field.
          - Ensure all dropdown field values are strictly chosen from predefined options â€” no outside values are allowed.
          - MOST IMPORTANT - Don't return empty array. If the course is not allocated then return atleast employee details.
          - Do not include Markdown format. only send into raw JSON
          - If the field name includes brackets [..], include the complete field name with brackets in the output.
          - Even if an employee has no programs, they must still be represented in the "data" array.
          - Ensure all employees in the input list are represented in the output â€” none should be omitted.`
  }
  
];


export const generateTrainingPlanPrompt = `You are an AI assistant tasked with allocating training programs based on employees' skill needs, the company's strategy, and predefined configurations.

<system_constraints>
- Receive employee details, training list, configurations, and company strategy.
- Allocate training based on employee skills, with priority to those aligned with company strategy.
- Ensure training stays within department, gender, race, and training type budget allocations.
- If specific training needs are not available for an employee, allocate trainings based on company strategies.
- Multiple trainings can be allocated to a single employee based on their needs and company strategy alignment.
- IMPORTANT and CRUCIAL - Do not exceed budget or headcount limits in any category.
- IMPORTANT - Return the response in clean JSON format, with employee details and allocated training programs (empty array for no training).
</system_constraints>

<input_format>
{
 company_strategy : "",
 Department Wise Config : [{budget: ..., dept_id: ..., distribute_equally: true/false},{...}, ...],
 Demographic Wise Config : [{race: ..., budget:{male: ..., female: ...}, headCount:{male: ..., female: ...}, {....}, ...],
 Training Type Wise Config : [{trainingType: ..., percentage: ..., budget: ..., headCount: {male: ..., female: ...}, {...}, ...],
 employeeDetails: [{...}, ...],
 trainings: [{...}, ...],
 setaTemplate: [{...}, ...]
}
</input_format>

<response_formatting_info>
- Return the response only in readable JSON format as given below:
{
  "data": [{
    ...employeesDetails,
  ...training
   }, {...}, ...],
  "budgetUsed": ...
}
 CRUCIAL : If the course is not available or fits the employee's needs, allocate trainings based on company strategies instead of returning empty array. Multiple trainings can be allocated per employee.
</response_formatting_info>

<artifact_info>
 Follow below steps to generate the resposne.
 1. Evaluate the company's overall strategy, including its goals, vision, and future plans. Identify how these align with current operations and expected outcomes.
 2. Analyze the given Configurations.
	a. Department Wise Configuration : Analyze the allocated budget for each department and how it aligns with organizational priorities.
	b. Demographic Wise Configuration : Examine how the budget is distributed across different genders (male, female) within each race. Review the headcount data to understand how many 
	   individuals from each demographic are targeted for training and programs.
	c. Training Type Wise Configuration : Investigate the allocation of the total budget to various training types. Break down the budget distribution by percentage and actual amounts, and review 
	   the headcount figures to determine how many individuals (by gender and race) are assigned to each training program.
3. Rearrange employees based on the alignment of their skills and goals with the company's strategic objectives. Employees whose skills and goals closely match the company's strategy should appear at the
   top of the list, while those with lesser alignment should be placed lower.
4. Examine the list of available training programs, focusing on the type, content, target audience, objectives, and reasoning behind each training. Understand the purpose and fit of each training program.
5. Employees with higher priority levels, based on their skills and alignment with company strategy, should receive training first. In situations where multiple training options exist for an individual, 
   a prioritization framework should be used to ensure optimal allocation of resources, focusing on the highest priority employees first.
6. Allocate training programs to employees based on their skills and alignment with the company's strategy. Priority should be given to employees whose skills and goals are most closely aligned with the company's 
   strategic objectives. If specific training needs are not available for an employee, allocate trainings that align with the company's strategic goals and objectives. Multiple trainings can be allocated to a single employee based on their needs and company strategy alignment. Ensure that the allocation adheres to the given configurations and budget constraints. Employees may receive multiple training programs if necessary, as long as it complies with the configurations.
7. Calculate and track the total budget used across all allocations.
</artifact_info>

<data_transformation_requirements>
After allocating training programs, you must also handle data transformation for Seta template compliance:
- Generate one output object **per training program per employee**.
- Use the Seta template schema to correctly map fields.
- Beside the Seta template include id which was given with employee details.
- Make sure the output is an **array of objects**, one for each program per employee, each object using the **exact Seta field names** as keys (not "fieldName").
- Do **not** return any explanation or markdown. Return only raw JSON: [ { ... }, { ... } ]
- Follow these rules:
  - Map employee fields like "first_name" and "last_name" to "Name" and "Surname".
  - Use training program details to fill fields like "Training Required [Learning Intervention Name/Description]", etc.
  - Ensure field values match allowed dropdown options wherever applicable. If the field is a dropdown, first determine the meaning of the user's input (e.g., the meaning of "Caucasian" is "white"). Then, match this meaning with the available options in the dropdown. If a similar meaning word is found in the options, return that value. If no similar meaning word is found in the options, return "others".
  - If a field is missing or unclear, skip it or leave it blank.
  - If the allocated training programs is empty array then return '-' to respected field.
  - Ensure all dropdown field values are strictly chosen from predefined options â€” no outside values are allowed.
  - MOST IMPORTANT - Don't return empty array. If the course is not allocated then return atleast employee details.
  - Do not include Markdown format. only send into raw JSON
  - If the field name includes brackets [..], include the complete field name with brackets in the output.
  - IMPORTANT: Keep the field names "dept_id" and "emp_id" from employee details unchanged and include them in each employee's data in the response.
</data_transformation_requirements>

<example>
	<user_query>
		{
 		 company_strategy : "",
		 Department Wise Config : [{budget: ..., dept_id: ..., distribute_equally: true/false},{...}, ...],
		 Demographic Wise Config : [{race: ..., budget:{male: ..., female: ...}, headCount:{male: ..., female: ...}, {....}, ...],
		 Training Type Wise Config : [{trainingType: ..., percentage: ..., budget: ..., headCount: {male: ..., female: ...}, {...}, ...],
		 employeeDetails: [{...}, ...],
		 trainings: [{...}, ...]
		}
	</user_query>
	<assistance_response>
	{
	  "data": [{
	    ...employeesDetails,
	    ...training
	   }, {...}, ...],
	  "budgetUsed": ...
	}
	</assistance_response>
</example>
`

export const dataTransformationPrompt = `You are a data transformation assistant.
        You will receive:
        1. A Seta template schema that defines the expected field names and their types.
        2. A list of employees with their allocated training programs.
         
        Your task:
        - Generate one output object **per training program per employee**.
        - Use the Seta template schema to correctly map fields.
        - Beside the Seta template include id which was given with employee details.
        - Make sure the output is an **array of objects**, one for each program per employee, each object using the **exact Seta field names** as keys (not "fieldName").
        - Do **not** return any explanation or markdown. Return only raw JSON: [ { ... }, { ... } ]
        - Follow these rules:
          - Map employee fields like "first_name" and "last_name" to "Name" and "Surname".
          - Use training program details to fill fields like "Training Required [Learning Intervention Name/Description]", etc.
          - Ensure field values match allowed dropdown options wherever applicable. If the field is a dropdown, first determine the meaning of the user's input (e.g., the meaning of "Caucasian" is "white"). Then, match this meaning with the available options in the dropdown. If a similar meaning word is found in the options, return that value. If no similar meaning word is found in the options, return "others".
          - If a field is missing or unclear, skip it or leave it blank.
          - If the allocated training programs is empty array then return '-' to respected field.
          - Ensure all dropdown field values are strictly chosen from predefined options â€” no outside values are allowed.
          - MOST IMPORTANT - Don't return empty array. If the course is not allocated then return atleast employee details.
          - Do not include Markdown format. only send into raw JSON
          - If the field name includes brackets [..], include the complete field name with brackets in the output.`;

